import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaveButton from "../../components/button/SaveButton";
import ReportButton from "../../components/button/ReportButton";
import WorkspaceReportPanel from "../../components/panel/WorkspaceReportPanel";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import { useAuth } from "../../authentication/AuthProvider";
import ExportProductPPTButton from "../../components/export/ExportProductPPTButton";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { EnvFactor } from "../../db_access/env_factor";
import { Process } from "../../db_access/process";
import { Product } from "../../db_access/product";
import Footprint from "../../environment-page-components/Footprint";
import LifeCycleAssessment from "../../environment-page-components/LifeCycleAssessment";
import LifeCycleInventory from "../../environment-page-components/LifeCycleInventory";
import ProductInfo from "../../environment-page-components/ProductInfo";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import ScopeAndGoals from "../../environment-page-components/ScopeAndGoals";
import SupplierInfo from "../../environment-page-components/SupplierInfo";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import ProductInsight from "./Workspace/ProductInsight";
import EvidenceRepository from "../../organizational-page-components/EvidenceRepository";
import routing from "../../routing";
import "../../style/pages-style/Supplier.css";

function ProductWorkspace() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const { isAIInsightEnabled } = useAuth();
  const { selectedProductIds, setIsLoading, setIsSaveClicked, setIsSaveVisible, refresh, setLeftSidebarCollapse } = useContextProvider();
  const navigate = useNavigate();
  const pageType = "supplier";
  const LifeCycleAssessmentRef = useRef();
  const [processes, setProcesses] = useState([]);
  const [envfactors, setEnvfactors] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState();
  const product_info = { label: "Product Info", value: "Product Info" };
  //const supplier_info = { label: "Supplier Info", value: "Supplier Info" };
  const lca = { label: "Assessments", value: "Assessments" };
  const result = { label: "Results", value: "Results" };
  const evidence = { label: "Evidence Repository", value: "Evidence Repository" };
  const tabs = [product_info, lca, result, evidence];
  const [activeTab, setActiveTab] = useState(product_info.value);

  // Auto-collapse sidebar on entering workspace (particularly on Assessments tab) until user manually toggles it
  useEffect(() => {
    setLeftSidebarCollapse(true);
  }, [setLeftSidebarCollapse]);

  useEffect(() => {
    if (activeTab === lca.value) {
      setLeftSidebarCollapse(true);
    }
  }, [activeTab, lca.value, setLeftSidebarCollapse]);

  useEffect(() => {
    if (selectedProductIds.length !== 1) {
      navigate(routing.sector.products.dashboard);
    } else {
      Product.get_by_id(selectedProductIds[0])
        .then((res) => res.json())
        .then((data) => {
          setSelectedProduct(data[0]);
          setIsSaveVisible(false);
          setIsLoading(false);
          setIsSaveClicked(false);
        });
    }
  }, [selectedProductIds, refresh, navigate, setIsLoading, setIsSaveClicked, setIsSaveVisible]);
  useEffect(() => {
    if (!selectedProduct) {
      setProcesses([]);
      return;
    }
    Process.get_by_product_ids([selectedProduct.id])
      .then((res) => res.json())
      .then((data) => setProcesses(data));
  }, [selectedProduct]);
  useEffect(() => {
    if (!processes || processes.length === 0) {
      setEnvfactors([]);
      return;
    }
    EnvFactor.get_by_process_ids(processes.map((p) => p.id))
      .then((res) => res.json())
      .then((data) => setEnvfactors(data));
  }, [processes]);

  return (
    <MainContainer>
      <Box sx={{ display: selectedProduct ? "block" : "none" }}>
        {/* ── Workspace Page Heading & Action Bar (Combined) ─────────── */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          gap: 3
        }}>
          {/* Segmented Tabs — left-aligned, full-width up to 800px */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <Box sx={{ width: "100%", maxWidth: "800px" }}>
              <SegmentedTabs
                options={tabs}
                selected={activeTab}
                setSelected={setActiveTab}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <ReportButton
              active={activeTab === "LCA Report"}
              onClick={() => setActiveTab("LCA Report")}
              label="LCA Report"
            />
          </Box>
        </Box>
        <Box sx={{ minHeight: "600px", minWidth: 0 }}>
          <Box sx={{
            display: activeTab === product_info.value ? "flex" : "none",
            flexDirection: "column",
            gap: 3,
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <ProductInfo />
            <SupplierInfo type={Product.label} selectedRow={selectedProduct} />
          </Box>
          <Box sx={{
            display: activeTab === lca.value ? "flex" : "none",
            flexDirection: "column",
            gap: 3,
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <ScopeAndGoals pageType={Product.value} selectedRow={selectedProduct} />
            <LifeCycleInventory selectedID={selectedProduct?.id} isSupplier={true} isReuse={false} />
          </Box>
          <Box sx={{
            display: activeTab === result.value ? "flex" : "none",
            flexDirection: "column",
            minWidth: 0,
            gap: 3,
            animation: "org-fadeInUp 0.4s ease"
          }}>
            <Box sx={{ display: selectedProduct?.is_epd !== 1 ? "flex" : "none", flexDirection: "column", gap: 3, minWidth: 0 }}>
              <Footprint processData={processes} pageType={pageType} />
              <SankeyDiagram processData={processes} envfactors={envfactors} />
            </Box>
            <SystemBoundary processData={processes} isEpd={selectedProduct?.is_epd} />
            <LifeCycleAssessment ref={LifeCycleAssessmentRef} selectedRow={selectedProduct} LCA_type={Product.label} />
            {isAIInsightEnabled && <ProductInsight />}
          </Box>
          {activeTab === evidence.value && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <EvidenceRepository entityId={selectedProduct?.id} idColumnName="product_id" />
            </Box>
          )}
          {activeTab === "LCA Report" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <WorkspaceReportPanel
                entity={selectedProduct}
                type="Product"
                processes={processes}
                envfactors={envfactors}
              />
            </Box>
          )}
        </Box>
      </Box>
      <SaveButton />
    </MainContainer>
  );
}

export default ProductWorkspace;
